/**
 * AdminLeadsPage — NTN Staff inbox for captured leads (Request Info,
 * Schedule Demo, Request Pricing). Pipeline-style header counts,
 * filterable list, drawer with full timeline + status mover + notes.
 */
import { useEffect, useMemo, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import {
  Inbox, PhoneCall, CalendarCheck2, FileSignature, Trophy, XCircle,
  Search, ExternalLink, Mail, Building2, Hash, Clock, MessageSquarePlus,
} from "lucide-react";

const STATUSES = [
  { key: "new",             label: "New",             icon: Inbox,          tone: "slate"   },
  { key: "contacted",       label: "Contacted",       icon: PhoneCall,      tone: "blue"    },
  { key: "demo_scheduled",  label: "Demo Scheduled",  icon: CalendarCheck2, tone: "violet"  },
  { key: "proposal_sent",   label: "Proposal Sent",   icon: FileSignature,  tone: "amber"   },
  { key: "won",             label: "Won",             icon: Trophy,         tone: "emerald" },
  { key: "lost",            label: "Lost",            icon: XCircle,        tone: "rose"    },
];

const TONE_BG = {
  slate:   "bg-slate-100 text-slate-700 ring-slate-200",
  blue:    "bg-blue-50 text-blue-700 ring-blue-200",
  violet:  "bg-violet-50 text-violet-700 ring-violet-200",
  amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rose:    "bg-rose-50 text-rose-700 ring-rose-200",
};
const TONE_DOT = {
  slate:   "bg-slate-400",
  blue:    "bg-blue-500",
  violet:  "bg-violet-500",
  amber:   "bg-amber-500",
  emerald: "bg-emerald-500",
  rose:    "bg-rose-500",
};

const KIND_LABEL = {
  info: "Request Information",
  demo: "Schedule a Demo",
  pricing: "Request Pricing",
};

const statusOf = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [filter, setFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        apiClient.get("/leads"),
        apiClient.get("/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [l, s] = await Promise.all([
          apiClient.get("/leads"),
          apiClient.get("/leads/stats"),
        ]);
        if (cancelled) return;
        setLeads(l.data);
        setStats(s.data);
        setError("");
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (kindFilter !== "all" && l.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
        (l.company || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q)
      );
    });
  }, [leads, filter, kindFilter, search]);

  const openLead = leads.find((l) => l.id === openId) || null;

  return (
    <AppShell>
      <div className="mb-8" data-testid="admin-leads-page">
        <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 mb-2">NTN Verify Hub · Sales</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-slate-900" data-testid="admin-leads-title">
          Leads Inbox
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Capture, qualify, and convert public inquiries from Request Information,
          Schedule a Demo, and Request Pricing forms.
        </p>
      </div>

      {/* Pipeline header */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2.5 mb-6" data-testid="leads-pipeline">
        <PipelineCard
          testid="pipeline-all"
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label="All Leads"
          count={stats.total || 0}
          dot="bg-slate-900"
        />
        {STATUSES.map((s) => (
          <PipelineCard
            key={s.key}
            testid={`pipeline-${s.key}`}
            active={filter === s.key}
            onClick={() => setFilter(s.key)}
            label={s.label}
            count={stats[s.key] || 0}
            dot={TONE_DOT[s.tone]}
            Icon={s.icon}
          />
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-md ring-1 ring-slate-200 px-4 py-3 mb-5 flex flex-wrap items-center gap-3" data-testid="leads-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, or email"
            className="pl-9 h-9 rounded-md"
            data-testid="leads-search"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">Source</span>
          {[
            { k: "all", l: "All" },
            { k: "info", l: "Information" },
            { k: "demo", l: "Demo" },
            { k: "pricing", l: "Pricing" },
          ].map((opt) => (
            <button
              key={opt.k}
              data-testid={`kind-filter-${opt.k}`}
              onClick={() => setKindFilter(opt.k)}
              className={`px-2.5 py-1 rounded-full text-xs transition ${
                kindFilter === opt.k
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500" data-testid="leads-count">
          {filtered.length} of {leads.length}
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 ring-1 ring-rose-200 text-rose-800 text-sm rounded-md px-4 py-3 mb-4" data-testid="leads-error">{error}</div>
      )}

      {/* List */}
      <div className="bg-white rounded-md ring-1 ring-slate-200 overflow-hidden" data-testid="leads-list">
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="leads-loading">Loading leads…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="leads-empty">
            <Inbox className="h-9 w-9 mx-auto text-slate-300" />
            <div className="mt-3 text-sm font-semibold text-slate-700">No leads match.</div>
            <div className="text-xs text-slate-500 mt-1">
              {leads.length === 0 ? "Submissions from public forms will appear here." : "Try adjusting filters."}
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((l) => {
              const st = statusOf(l.status);
              const Icon = st.icon;
              return (
                <li
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  onClick={() => setOpenId(l.id)}
                  className="px-5 py-4 hover:bg-slate-50 cursor-pointer flex items-center gap-4"
                >
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ring-1 ${TONE_BG[st.tone]}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{l.first_name} {l.last_name}</span>
                      <Badge variant="outline" className="rounded-sm text-[9px] uppercase tracking-wider border-slate-300 text-slate-600 font-mono">
                        {KIND_LABEL[l.kind] || l.kind}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-600 truncate mt-0.5 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {l.company}</span>
                      <span className="inline-flex items-center gap-1 text-slate-500"><Mail className="h-3 w-3" /> {l.email}</span>
                      {l.units_managed && (
                        <span className="inline-flex items-center gap-1 text-slate-500"><Hash className="h-3 w-3" /> {l.units_managed} units</span>
                      )}
                    </div>
                  </div>
                  <Badge
                    className={`rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 ring-1 border-0 ${TONE_BG[st.tone]}`}
                    data-testid={`lead-status-${l.id}`}
                  >
                    {st.label}
                  </Badge>
                  <div className="text-xs text-slate-500 w-32 text-right hidden md:flex items-center justify-end gap-1.5">
                    <Clock className="h-3 w-3" /> {fmtDate(l.created_at)}
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-slate-600" />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <LeadDrawer
        lead={openLead}
        onClose={() => setOpenId(null)}
        onUpdated={(updated) => {
          setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
          load();
        }}
      />
    </AppShell>
  );
}

function PipelineCard({ active, onClick, label, count, dot, Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white rounded-md px-3.5 py-3 ring-1 transition shadow-sm ${
        active ? "ring-slate-900 shadow-md" : "ring-slate-200 hover:ring-slate-300"
      }`}
    >
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-500" />}
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <span className="font-serif text-2xl text-slate-900">{count}</span>
      </div>
    </button>
  );
}

function LeadDrawer({ lead, onClose, onUpdated }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  if (!lead) return null;
  const st = statusOf(lead.status);

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/leads/${lead.id}`, patch);
      onUpdated(data);
      if (patch.note) setNote("");
      toast.success("Lead updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const events = (lead.events || []).slice().reverse();

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-xl w-full overflow-y-auto" data-testid="lead-drawer">
        <SheetHeader>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500" data-testid="lead-drawer-kind">
            {KIND_LABEL[lead.kind] || lead.kind}
          </div>
          <SheetTitle className="font-serif text-2xl text-slate-900" data-testid="lead-drawer-name">
            {lead.first_name} {lead.last_name}
          </SheetTitle>
          <SheetDescription className="text-slate-600">
            <span className="font-semibold text-slate-800">{lead.company}</span>
            {lead.units_managed && <> · <span>{lead.units_managed} units</span></>}
          </SheetDescription>
        </SheetHeader>

        {/* Contact details */}
        <div className="mt-6 rounded-md ring-1 ring-slate-200 divide-y divide-slate-100 text-sm">
          <Row label="Email"><a className="text-slate-900 hover:underline" href={`mailto:${lead.email}`} data-testid="lead-drawer-email">{lead.email}</a></Row>
          {lead.phone && <Row label="Phone"><a className="text-slate-900 hover:underline" href={`tel:${lead.phone}`}>{lead.phone}</a></Row>}
          {lead.preferred_contact && <Row label="Preferred contact">{lead.preferred_contact}</Row>}
          {lead.preferred_meeting_time && <Row label="Preferred meeting">{lead.preferred_meeting_time}</Row>}
          <Row label="Submitted">{fmtDateTime(lead.created_at)}</Row>
        </div>

        {/* Original message */}
        {lead.message && (
          <div className="mt-5 bg-amber-50 ring-1 ring-amber-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-2">Original message</div>
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="lead-drawer-message">{lead.message}</p>
          </div>
        )}

        {/* Status mover */}
        <div className="mt-7" data-testid="lead-status-mover">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Move to</div>
          <div className="grid grid-cols-3 gap-2">
            {STATUSES.map((s) => {
              const Icon = s.icon;
              const active = lead.status === s.key;
              return (
                <button
                  key={s.key}
                  disabled={saving || active}
                  onClick={() => update({ status: s.key })}
                  data-testid={`set-status-${s.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition ring-1 ${
                    active
                      ? `${TONE_BG[s.tone]} ring-current cursor-default`
                      : "bg-white ring-slate-200 text-slate-700 hover:bg-slate-50"
                  } ${saving ? "opacity-60" : ""}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Current: <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ring-1 ${TONE_BG[st.tone]}`}>{st.label}</span>
          </div>
        </div>

        {/* Add note */}
        <div className="mt-7">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Add note</div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Logged a call, sent proposal, decision context…"
            rows={3}
            className="rounded-md text-sm"
            data-testid="lead-note-input"
          />
          <Button
            onClick={() => note.trim() && update({ note })}
            disabled={!note.trim() || saving}
            className="mt-2 h-9 rounded-md bg-slate-900 hover:bg-slate-800"
            data-testid="lead-note-submit"
          >
            <MessageSquarePlus className="h-4 w-4 mr-1.5" /> Save note
          </Button>
        </div>

        {/* Timeline */}
        <div className="mt-8 pb-6">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Activity</div>
          <ol className="space-y-3" data-testid="lead-timeline">
            {events.length === 0 ? (
              <li className="text-sm text-slate-500">No activity yet.</li>
            ) : events.map((ev) => (
              <li key={ev.id} className="bg-white ring-1 ring-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold text-slate-900">{ev.actor_name} <span className="font-normal text-slate-500">· {ev.actor_role}</span></div>
                  <div className="text-[10px] text-slate-500">{fmtDateTime(ev.at)}</div>
                </div>
                <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{ev.body}</div>
              </li>
            ))}
          </ol>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 w-32 shrink-0">{label}</span>
      <span className="text-sm text-slate-800 min-w-0 truncate">{children}</span>
    </div>
  );
}
